import { Note } from "../types/sound";
import { transposeNote, noteToMidi } from "./noteUtils";

// Common musical intervals in semitones
export const INTERVALS = {
    UNISON: 0,
    MINOR_SECOND: 1,
    MAJOR_SECOND: 2,
    MINOR_THIRD: 3,
    MAJOR_THIRD: 4,
    PERFECT_FOURTH: 5,
    TRITONE: 6,
    PERFECT_FIFTH: 7,
    MINOR_SIXTH: 8,
    MAJOR_SIXTH: 9,
    MINOR_SEVENTH: 10,
    MAJOR_SEVENTH: 11,
    OCTAVE: 12,
} as const;

export type IntervalName = keyof typeof INTERVALS;

// Chord types with their interval patterns (from root)
export const CHORD_TYPES = {
    MAJOR: [0, 4, 7], // Root, Major 3rd, Perfect 5th
    MINOR: [0, 3, 7], // Root, Minor 3rd, Perfect 5th
    DIMINISHED: [0, 3, 6], // Root, Minor 3rd, Tritone
    AUGMENTED: [0, 4, 8], // Root, Major 3rd, Minor 6th
    MAJOR_7: [0, 4, 7, 11], // Major + Major 7th
    MINOR_7: [0, 3, 7, 10], // Minor + Minor 7th
    DOMINANT_7: [0, 4, 7, 10], // Major + Minor 7th
    DIMINISHED_7: [0, 3, 6, 9], // Diminished + Diminished 7th
    HALF_DIMINISHED_7: [0, 3, 6, 10], // Diminished + Minor 7th
    SUS2: [0, 2, 7], // Root, Major 2nd, Perfect 5th
    SUS4: [0, 5, 7], // Root, Perfect 4th, Perfect 5th
} as const;

export type ChordType = keyof typeof CHORD_TYPES;

// Common chord progressions (using Roman numeral analysis)
export const PROGRESSIONS = {
    // Major key progressions
    "I-V-vi-IV": [1, 5, 6, 4], // Very common pop progression
    "vi-IV-I-V": [6, 4, 1, 5], // Another common pop progression
    "I-vi-IV-V": [1, 6, 4, 5], // Classic 50s progression
    "ii-V-I": [2, 5, 1], // Jazz turnaround
    "I-IV-V-I": [1, 4, 5, 1], // Basic cadence
    "vi-V-IV-V": [6, 5, 4, 5], // Deceptive resolution
    "I-V-vi-iii-IV-I-IV-V": [1, 5, 6, 3, 4, 1, 4, 5], // Extended progression

    // Minor key progressions (relative to major)
    "i-VII-VI-VII": [6, 5, 4, 5], // Natural minor progression
    "i-iv-V-i": [6, 2, 5, 6], // Harmonic minor progression
    "i-VI-III-VII": [6, 4, 1, 5], // Another minor progression
} as const;

export type ProgressionName = keyof typeof PROGRESSIONS;

// Scale degrees for major scale
export const SCALE_DEGREES = {
    TONIC: 1,
    SUPERTONIC: 2,
    MEDIANT: 3,
    SUBDOMINANT: 4,
    DOMINANT: 5,
    SUBMEDIANT: 6,
    LEADING_TONE: 7,
} as const;

/**
 * Build an interval from a root note
 */
export function buildInterval(root: Note, intervalName: IntervalName): [Note, Note] {
    const semitones = INTERVALS[intervalName];
    const upperNote = transposeNote(root, semitones);
    return [root, upperNote];
}

/**
 * Build a chord from a root note and chord type
 */
export function buildChord(root: Note, chordType: ChordType): Note[] {
    const intervals = CHORD_TYPES[chordType];
    return intervals.map((semitones) => transposeNote(root, semitones));
}

/**
 * Build a chord progression from a root note and progression pattern
 */
export function buildProgression(
    key: Note,
    progressionName: ProgressionName,
    chordType: ChordType = "MAJOR"
): Note[][] {
    const pattern = PROGRESSIONS[progressionName];
    const scale = buildMajorScale(key);

    return pattern.map((degree) => {
        const chordRoot = scale[degree - 1]; // Convert to 0-based index
        return buildChord(chordRoot, chordType);
    });
}

/**
 * Build a major scale from a root note
 */
export function buildMajorScale(root: Note): Note[] {
    const majorScalePattern = [0, 2, 4, 5, 7, 9, 11]; // Whole-whole-half-whole-whole-whole-half
    return majorScalePattern.map((semitones) => transposeNote(root, semitones));
}

/**
 * Build a natural minor scale from a root note
 */
export function buildMinorScale(root: Note): Note[] {
    const minorScalePattern = [0, 2, 3, 5, 7, 8, 10]; // Natural minor pattern
    return minorScalePattern.map((semitones) => transposeNote(root, semitones));
}

/**
 * Get the interval name between two notes
 */
export function getIntervalName(note1: Note, note2: Note): IntervalName | null {
    const midi1 = noteToMidi(note1);
    const midi2 = noteToMidi(note2);
    const semitones = Math.abs(midi2 - midi1) % 12; // Reduce to within one octave

    for (const [name, value] of Object.entries(INTERVALS)) {
        if (value === semitones) {
            return name as IntervalName;
        }
    }

    return null;
}

/**
 * Identify a chord type from an array of notes
 */
export function identifyChord(notes: Note[]): ChordType | null {
    if (notes.length < 2) return null;

    // Normalize to root position by finding the lowest note
    const sortedNotes = notes.sort((a, b) => noteToMidi(a) - noteToMidi(b));
    const root = sortedNotes[0];
    const rootMidi = noteToMidi(root);

    // Calculate intervals from root
    const intervals = sortedNotes
        .map((note) => (noteToMidi(note) - rootMidi) % 12)
        .sort((a, b) => a - b);

    // Match against known chord patterns
    for (const [chordType, pattern] of Object.entries(CHORD_TYPES)) {
        if (
            intervals.length === pattern.length &&
            intervals.every((interval, index) => interval === pattern[index])
        ) {
            return chordType as ChordType;
        }
    }

    return null;
}

/**
 * Generate a random interval within specified range
 */
export function getRandomInterval(
    minSemitones: number = 1,
    maxSemitones: number = 12
): IntervalName {
    const validIntervals = Object.entries(INTERVALS).filter(
        ([_, semitones]) => semitones >= minSemitones && semitones <= maxSemitones
    );

    const randomIndex = Math.floor(Math.random() * validIntervals.length);
    return validIntervals[randomIndex][0] as IntervalName;
}

/**
 * Generate a random chord type
 */
export function getRandomChordType(): ChordType {
    const chordTypes = Object.keys(CHORD_TYPES) as ChordType[];
    const randomIndex = Math.floor(Math.random() * chordTypes.length);
    return chordTypes[randomIndex];
}

/**
 * Generate a random progression
 */
export function getRandomProgression(): ProgressionName {
    const progressions = Object.keys(PROGRESSIONS) as ProgressionName[];
    const randomIndex = Math.floor(Math.random() * progressions.length);
    return progressions[randomIndex];
}

/**
 * Invert a chord (move the lowest note up an octave)
 */
export function invertChord(chord: Note[], inversion: number = 1): Note[] {
    if (inversion <= 0 || chord.length === 0) return [...chord];

    const sortedChord = chord.sort((a, b) => noteToMidi(a) - noteToMidi(b));
    const result = [...sortedChord];

    for (let i = 0; i < inversion && i < result.length; i++) {
        const noteToMove = result.shift()!;
        const transposedNote = transposeNote(noteToMove, 12); // Move up an octave
        result.push(transposedNote);
    }

    return result.sort((a, b) => noteToMidi(a) - noteToMidi(b));
}

/**
 * Get chord progressions in a specific key with proper chord qualities
 */
export function buildDiatonicProgression(
    key: Note,
    progressionName: ProgressionName
): { chord: Note[]; degree: number; quality: ChordType }[] {
    const pattern = PROGRESSIONS[progressionName];
    const scale = buildMajorScale(key);

    // Major scale chord qualities: I-ii-iii-IV-V-vi-vii°
    const majorKeyQualities: ChordType[] = [
        "MAJOR", // I
        "MINOR", // ii
        "MINOR", // iii
        "MAJOR", // IV
        "MAJOR", // V
        "MINOR", // vi
        "DIMINISHED", // vii°
    ];

    return pattern.map((degree) => {
        const chordRoot = scale[degree - 1];
        const quality = majorKeyQualities[degree - 1];
        const chord = buildChord(chordRoot, quality);

        return {
            chord,
            degree,
            quality,
        };
    });
}

/**
 * Generate practice intervals for ear training
 */
export function generateIntervalExercise(
    root: Note,
    includedIntervals: IntervalName[] = Object.keys(INTERVALS) as IntervalName[]
): { interval: [Note, Note]; name: IntervalName } {
    const randomInterval = includedIntervals[Math.floor(Math.random() * includedIntervals.length)];
    const intervalNotes = buildInterval(root, randomInterval);

    return {
        interval: intervalNotes,
        name: randomInterval,
    };
}

/**
 * Generate chord identification exercise
 */
/**
 * Format a chord type name for display in a more musical way
 */
export function formatChordName(chordType: ChordType): string {
    const chordDisplayMap: Record<ChordType, string> = {
        MAJOR: "Major",
        MINOR: "Minor",
        DIMINISHED: "Diminished",
        AUGMENTED: "Augmented",
        MAJOR_7: "Major 7th",
        MINOR_7: "Minor 7th",
        DOMINANT_7: "Dominant 7th",
        DIMINISHED_7: "Diminished 7th",
        HALF_DIMINISHED_7: "Half Diminished 7th",
        SUS2: "Sus2",
        SUS4: "Sus4"
    };

    return chordDisplayMap[chordType] || chordType;
}

export function generateChordExercise(
    root: Note,
    includedChords: ChordType[] = ["MAJOR", "MINOR", "DIMINISHED", "AUGMENTED"]
): { chord: Note[]; type: ChordType } {
    const randomChordType = includedChords[Math.floor(Math.random() * includedChords.length)];
    const chord = buildChord(root, randomChordType);

    return {
        chord,
        type: randomChordType,
    };
}

/**
 * Build a harmonic minor scale from a root note
 */
export function buildHarmonicMinorScale(root: Note): Note[] {
    const harmonicMinorPattern = [0, 2, 3, 5, 7, 8, 11]; // Natural minor with raised 7th
    return harmonicMinorPattern.map((semitones) => transposeNote(root, semitones));
}

export type ScaleType = "MAJOR" | "NATURAL_MINOR" | "HARMONIC_MINOR";

/**
 * Build a scale of a specified type from a root note
 */
export function buildScale(root: Note, scaleType: ScaleType): Note[] {
    switch (scaleType) {
        case "MAJOR":
            return buildMajorScale(root);
        case "NATURAL_MINOR":
            return buildMinorScale(root);
        case "HARMONIC_MINOR":
            return buildHarmonicMinorScale(root);
        default:
            throw new Error(`Unknown scale type: ${scaleType}`);
    }
}

/**
 * Generate a random melody based on scale degrees
 */
export function generateRandomMelody(
    root: Note,
    scaleType: ScaleType,
    scaleDegrees: number[],
    melodyLength: number = 6
): { melody: Note[]; sequence: number[] } {
    const scale = buildScale(root, scaleType);
    const melody: Note[] = [];
    const sequence: number[] = [];

    // Generate random scale degrees for the melody from available degrees
    for (let i = 0; i < melodyLength; i++) {
        const randomDegreeIndex = Math.floor(Math.random() * scaleDegrees.length);
        const scaleDegree = scaleDegrees[randomDegreeIndex];
        melody.push(scale[scaleDegree - 1]);
        sequence.push(scaleDegree);
    }

    return { melody, sequence };
}

/**
 * Calculate similarity between two sequences (0 = completely different, 1 = identical)
 */
function calculateSequenceSimilarity(seq1: number[], seq2: number[]): number {
    if (seq1.length !== seq2.length) return 0;
    
    let matches = 0;
    for (let i = 0; i < seq1.length; i++) {
        if (seq1[i] === seq2[i]) matches++;
    }
    
    return matches / seq1.length;
}

/**
 * Generate random incorrect scale degree sequences for multiple choice
 * Returns sequences that use different combinations from the available scale degrees
 * 
 * @param correctSequence - The correct answer sequence
 * @param availableDegrees - Pool of scale degrees to choose from
 * @param count - Number of incorrect sequences to generate (default: 4)
 * @param sequenceLength - Length of each sequence
 * @param difficulty - Difficulty level 1-3, where higher = more similar to correct answer
 */
export function generateIncorrectSequences(
    correctSequence: number[],
    availableDegrees: number[],
    count: number = 4,
    sequenceLength: number = 6,
    difficulty: number = 1
): number[][] {
    const incorrectSequences: number[][] = [];
    const seenSequences = new Set<string>();
    
    // Add correct sequence to seen set
    seenSequences.add(correctSequence.join(','));

    // Calculate target similarity based on difficulty (1-3)
    // Level 1: 0-20% similar (very different)
    // Level 2: 20-50% similar (medium)
    // Level 3: 50-80% similar (very tricky)
    const difficultyRanges = [
        { min: 0, max: 0.2 },    // Level 1
        { min: 0.2, max: 0.5 },  // Level 2
        { min: 0.5, max: 0.8 },  // Level 3
    ];
    
    const clampedDifficulty = Math.max(1, Math.min(3, difficulty));
    const { min: minSimilarity, max: maxSimilarity } = difficultyRanges[clampedDifficulty - 1];

    // Maximum attempts to prevent infinite loop
    const maxAttempts = count * 200;
    let attempts = 0;

    while (incorrectSequences.length < count && attempts < maxAttempts) {
        attempts++;
        
        const newSequence: number[] = [];
        
        // For higher difficulties, start with the correct sequence and modify it
        // For lower difficulties, generate more randomly
        const shouldStartFromCorrect = Math.random() < (difficulty / 3);
        
        if (shouldStartFromCorrect && difficulty >= 3) {
            // Start with correct sequence and modify some positions
            newSequence.push(...correctSequence);
            
            // Calculate how many positions to change based on target similarity
            const targetSimilarity = minSimilarity + Math.random() * (maxSimilarity - minSimilarity);
            const positionsToChange = Math.floor(sequenceLength * (1 - targetSimilarity));
            
            // Randomly select positions to change
            const positionsToModify = new Set<number>();
            while (positionsToModify.size < positionsToChange) {
                positionsToModify.add(Math.floor(Math.random() * sequenceLength));
            }
            
            // Change the selected positions
            positionsToModify.forEach(pos => {
                const currentDegree = newSequence[pos];
                // Try to pick a different degree
                const otherDegrees = availableDegrees.filter(d => d !== currentDegree);
                if (otherDegrees.length > 0) {
                    newSequence[pos] = otherDegrees[Math.floor(Math.random() * otherDegrees.length)];
                }
            });
        } else {
            // Generate more randomly
            for (let i = 0; i < sequenceLength; i++) {
                const randomDegreeIndex = Math.floor(Math.random() * availableDegrees.length);
                newSequence.push(availableDegrees[randomDegreeIndex]);
            }
        }

        const sequenceKey = newSequence.join(',');
        
        // Check if this sequence is unique
        if (!seenSequences.has(sequenceKey)) {
            // Check if similarity is within acceptable range
            const similarity = calculateSequenceSimilarity(newSequence, correctSequence);
            
            if (similarity >= minSimilarity && similarity <= maxSimilarity) {
                seenSequences.add(sequenceKey);
                incorrectSequences.push(newSequence);
            }
        }
    }

    // If we couldn't generate enough sequences with strict similarity requirements,
    // fall back to generating any unique sequences
    if (incorrectSequences.length < count) {
        attempts = 0;
        while (incorrectSequences.length < count && attempts < maxAttempts) {
            attempts++;
            
            const newSequence: number[] = [];
            for (let i = 0; i < sequenceLength; i++) {
                const randomDegreeIndex = Math.floor(Math.random() * availableDegrees.length);
                newSequence.push(availableDegrees[randomDegreeIndex]);
            }

            const sequenceKey = newSequence.join(',');
            
            if (!seenSequences.has(sequenceKey)) {
                seenSequences.add(sequenceKey);
                incorrectSequences.push(newSequence);
            }
        }
    }

    // If we still couldn't generate enough sequences, log a warning
    if (incorrectSequences.length < count) {
        console.warn(
            `Could only generate ${incorrectSequences.length} unique incorrect sequences out of ${count} requested. ` +
            `Consider using more available degrees or shorter sequences.`
        );
    }

    return incorrectSequences;
}

/**
 * Format a sequence of scale degrees for display
 */
export function formatScaleDegreeSequence(sequence: number[]): string {
    return sequence.join(", ");
}

/**
 * Get all available scale types for display
 */
export function getScaleTypeLabel(scaleType: ScaleType): string {
    const labels: Record<ScaleType, string> = {
        MAJOR: "Major",
        NATURAL_MINOR: "Natural Minor",
        HARMONIC_MINOR: "Harmonic Minor",
    };
    return labels[scaleType];
}

/**
 * Calculate similarity between two chord progressions (0 = completely different, 1 = identical)
 */
function calculateProgressionSimilarity(prog1: number[], prog2: number[]): number {
    if (prog1.length !== prog2.length) return 0;
    
    let matches = 0;
    for (let i = 0; i < prog1.length; i++) {
        if (prog1[i] === prog2[i]) matches++;
    }
    
    return matches / prog1.length;
}

/**
 * Generate random incorrect chord progressions for multiple choice
 * Similar to generateIncorrectSequences but for chord progressions
 * 
 * @param correctProgression - The correct answer progression (array of scale degrees)
 * @param availableChords - Pool of chord degrees to choose from (e.g., [1, 2, 3, 4, 5, 6, 7])
 * @param count - Number of incorrect progressions to generate (default: 4)
 * @param progressionLength - Length of each progression
 * @param difficulty - Difficulty level 1-3, where higher = more similar to correct answer
 */
export function generateIncorrectProgressions(
    correctProgression: number[],
    availableChords: number[],
    count: number = 4,
    progressionLength: number = 4,
    difficulty: number = 1
): number[][] {
    const incorrectProgressions: number[][] = [];
    const seenProgressions = new Set<string>();
    
    // Add correct progression to seen set
    seenProgressions.add(correctProgression.join(','));

    // Calculate target similarity based on difficulty (1-3)
    // Level 1: 0-20% similar (very different)
    // Level 2: 20-50% similar (medium)
    // Level 3: 50-75% similar (very tricky - keep lower than melodies since progressions are shorter)
    const difficultyRanges = [
        { min: 0, max: 0.2 },    // Level 1
        { min: 0.2, max: 0.5 },  // Level 2
        { min: 0.5, max: 0.75 }, // Level 3
    ];
    
    const clampedDifficulty = Math.max(1, Math.min(3, difficulty));
    const { min: minSimilarity, max: maxSimilarity } = difficultyRanges[clampedDifficulty - 1];

    // Maximum attempts to prevent infinite loop
    const maxAttempts = count * 200;
    let attempts = 0;

    while (incorrectProgressions.length < count && attempts < maxAttempts) {
        attempts++;
        
        const newProgression: number[] = [];
        
        // For higher difficulties, start with the correct progression and modify it
        // For lower difficulties, generate more randomly
        const shouldStartFromCorrect = Math.random() < (difficulty / 3);
        
        if (shouldStartFromCorrect && difficulty >= 3) {
            // Start with correct progression and modify some positions
            newProgression.push(...correctProgression);
            
            // Calculate how many positions to change based on target similarity
            const targetSimilarity = minSimilarity + Math.random() * (maxSimilarity - minSimilarity);
            const positionsToChange = Math.floor(progressionLength * (1 - targetSimilarity));
            
            // Randomly select positions to change
            const positionsToModify = new Set<number>();
            while (positionsToModify.size < positionsToChange) {
                positionsToModify.add(Math.floor(Math.random() * progressionLength));
            }
            
            // Change the selected positions
            positionsToModify.forEach(pos => {
                const currentChord = newProgression[pos];
                // Try to pick a different chord
                const otherChords = availableChords.filter(d => d !== currentChord);
                if (otherChords.length > 0) {
                    newProgression[pos] = otherChords[Math.floor(Math.random() * otherChords.length)];
                }
            });
        } else {
            // Generate more randomly
            for (let i = 0; i < progressionLength; i++) {
                const randomChordIndex = Math.floor(Math.random() * availableChords.length);
                newProgression.push(availableChords[randomChordIndex]);
            }
        }

        const progressionKey = newProgression.join(',');
        
        // Check if this progression is unique
        if (!seenProgressions.has(progressionKey)) {
            // Check if similarity is within acceptable range
            const similarity = calculateProgressionSimilarity(newProgression, correctProgression);
            
            if (similarity >= minSimilarity && similarity <= maxSimilarity) {
                seenProgressions.add(progressionKey);
                incorrectProgressions.push(newProgression);
            }
        }
    }

    // If we couldn't generate enough progressions with strict similarity requirements,
    // fall back to generating any unique progressions
    if (incorrectProgressions.length < count) {
        attempts = 0;
        while (incorrectProgressions.length < count && attempts < maxAttempts) {
            attempts++;
            
            const newProgression: number[] = [];
            for (let i = 0; i < progressionLength; i++) {
                const randomChordIndex = Math.floor(Math.random() * availableChords.length);
                newProgression.push(availableChords[randomChordIndex]);
            }

            const progressionKey = newProgression.join(',');
            
            if (!seenProgressions.has(progressionKey)) {
                seenProgressions.add(progressionKey);
                incorrectProgressions.push(newProgression);
            }
        }
    }

    // If we still couldn't generate enough progressions, log a warning
    if (incorrectProgressions.length < count) {
        console.warn(
            `Could only generate ${incorrectProgressions.length} unique incorrect progressions out of ${count} requested. ` +
            `Consider using more available chords or shorter progressions.`
        );
    }

    return incorrectProgressions;
}

/**
 * Format a chord progression using Roman numerals
 * @param progression - Array of scale degrees (1-7)
 * @param qualities - Optional array of chord qualities for proper display
 */
export function formatChordProgression(progression: number[], qualities?: string[]): string {
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
    
    // Major scale chord qualities: I-ii-iii-IV-V-vi-vii°
    const majorKeyDisplay = ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'];
    
    return progression.map((degree, index) => {
        if (qualities && qualities[index]) {
            return qualities[index];
        }
        // Use proper major key display (uppercase for major, lowercase for minor, ° for diminished)
        return majorKeyDisplay[degree - 1] || romanNumerals[degree - 1];
    }).join('-');
}
